// src/components/CoverCropperModal.js
/**
 * CoverCropperModal — 通用图片裁剪弹窗
 *
 * 同时服务于两种场景，通过 props 区分：
 *
 * ┌─────────────────┬──────────────────────────────────────────────────┐
 * │  场景           │  Props                                           │
 * ├─────────────────┼──────────────────────────────────────────────────┤
 * │ 项目封面        │ aspectRatio=16/9 (默认)  裁剪输出 1280×720        │
 * │ 项目海报        │ aspectRatio=9/16         裁剪输出 810×1440        │
 * └─────────────────┴──────────────────────────────────────────────────┘
 *
 * ⚠️ 修改海报裁剪比例时，需同步修改以下三处：
 *   1. EditorPage.js → posterCropUrl 对应的 <CoverCropperModal aspectRatio={9/16}>
 *   2. DashboardPage.js → cropImageUrl + posterMode 对应的 <CoverCropperModal aspectRatio={9/16}>
 *   3. DashboardPage.js → 浮动海报 div 的 style.aspectRatio: '9 / 16'
 *
 * 跨域处理：
 *   远程图片（OSS URL）会先 fetch 转为 blob URL，避免 canvas tainted（跨域污染），
 *   否则 getCroppedCanvas().toBlob() 会抛出 SecurityError。
 *
 * @param {string}   imageUrl    - 待裁剪图片的 URL（支持 OSS URL / blob: / data:）
 * @param {function} onClose     - 关闭弹窗回调
 * @param {function} onSave      - 裁剪完成回调，参数为 Blob (image/jpeg, quality=0.9)
 * @param {number}   [aspectRatio=16/9] - 裁剪比例，封面用 16/9，海报用 9/16
 * @param {string}   [title]     - 弹窗标题
 * @param {string}   [buttonText] - 确认按钮文字
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Cropper from 'react-cropper';
import "cropperjs/dist/cropper.css";
import { X, ZoomIn, ZoomOut, RotateCw, FlipHorizontal, FlipVertical, Image } from 'lucide-react';

const PRIMARY_COLOR = '#3b82f6';

const modalOverlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.85)', zIndex: 999999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backdropFilter: 'blur(5px)'
};

const modalContentStyle = {
  position: 'relative',
  width: '900px', height: 'min(650px, 85vh)',
  maxWidth: '95vw', maxHeight: '95vh',
  background: '#0f172a', borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
  boxShadow: '0 40px 80px rgba(0,0,0,0.8)', border: '1px solid var(--border-subtle)'
};

const modalHeaderStyle = {
  height: '56px',
  background: 'var(--toolbar-bg)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 20px', borderBottom: '1px solid #334155'
};

const modalCanvasStyle = {
  flex: 1,
  position: 'relative', background: '#000000', overflow: 'hidden'
};

const modalFooterStyle = {
  height: '72px',
  background: 'var(--toolbar-bg)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 24px', borderTop: '1px solid #334155'
};

const toolSectionStyle = {
  display: 'flex', alignItems: 'center', gap: '12px'
};

const iconBtnStyle = (isActive) => ({
  width: '40px', height: '40px',
  borderRadius: '8px',
  background: isActive ? PRIMARY_COLOR : 'rgba(255,255,255,0.05)',
  color: isActive ? '#fff' : 'var(--text-secondary)',
  border: isActive ? `1px solid ${PRIMARY_COLOR}` : '1px solid var(--border-subtle)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', transition: 'all 0.2s',
});

const actionBtnStyle = (variant) => ({
  padding: '0 24px', height: '40px', borderRadius: '8px', border: 'none',
  fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  background: variant === 'primary' ? PRIMARY_COLOR : 'var(--bg-hover)',
  color: variant === 'primary' ? '#fff' : '#cbd5e1',
});

const closeBtnStyle = {
  width: '32px', height: '32px', borderRadius: '6px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: 'var(--text-secondary)',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid var(--border-subtle)',
  transition: 'all 0.2s'
};

const sliderStyle = { width: '100px', cursor: 'pointer', height: '4px' };
const dividerStyle = { width: '1px', height: '32px', background: 'var(--border-strong)' };

export default function CoverCropperModal({ imageUrl, onClose, onSave, aspectRatio, title, buttonText }) {
  const cropperRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(0.5);

  // Configurable aspect ratio (default 16:9 for project covers)
  const COVER_ASPECT_RATIO = aspectRatio || 16 / 9;
  const modalTitle = title || '设置项目封面';
  const saveBtnText = buttonText || '设为封面';
  // 动态生成比例标签
  const ratioLabel = (() => {
    if (COVER_ASPECT_RATIO === 1) return '1:1';
    if (Math.abs(COVER_ASPECT_RATIO - 16/9) < 0.01) return '16:9';
    if (Math.abs(COVER_ASPECT_RATIO - 9/16) < 0.01) return '9:16';
    if (Math.abs(COVER_ASPECT_RATIO - 7/10) < 0.01) return '7:10';
    return COVER_ASPECT_RATIO < 1 ? `${Math.round(COVER_ASPECT_RATIO*16)}:16` : '16:9';
  })();

  // 🔧 将远程图片预转换为 blob URL，避免跨域 canvas 污染（tainted canvas）
  const [safeSrc, setSafeSrc] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!imageUrl) return;
    if (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:')) {
      setSafeSrc(imageUrl);
      return;
    }
    let cancelled = false;
    fetch(imageUrl)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.blob(); })
      .then(blob => {
        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setSafeSrc(url);
        }
      })
      .catch(() => {
        if (!cancelled) setSafeSrc(imageUrl);
      });
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [imageUrl]);

  const handleSave = useCallback(() => {
    if (cropperRef.current && cropperRef.current.cropper) {
      try {
        const outW = COVER_ASPECT_RATIO >= 1 ? 1280 : Math.round(1440 * COVER_ASPECT_RATIO);
        const outH = COVER_ASPECT_RATIO >= 1 ? 720 : 1440;
        cropperRef.current.cropper.getCroppedCanvas({
          width: outW,
          height: outH,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        }).toBlob((blob) => {
          if (blob) onSave(blob);
        }, 'image/jpeg', 0.9);
      } catch (err) {
        console.error('封面裁剪失败（canvas 可能被跨域污染）:', err);
        alert('裁剪失败：图片跨域限制，请刷新页面后重试');
      }
    }
  }, [COVER_ASPECT_RATIO, onSave]);

  const handleZoom = (e) => {
    const val = parseFloat(e.target.value);
    setZoomLevel(val);
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.zoomTo(0.1 + val * 2);
    }
  };

  const handleRotate = () => {
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.rotate(90);
    }
  };

  const handleFlipH = () => {
    if (cropperRef.current?.cropper) {
      const c = cropperRef.current.cropper;
      c.scaleX(-c.getData().scaleX || -1);
    }
  };

  const handleFlipV = () => {
    if (cropperRef.current?.cropper) {
      const c = cropperRef.current.cropper;
      c.scaleY(-c.getData().scaleY || -1);
    }
  };

  const handleReset = () => {
    setZoomLevel(0.5);
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.reset();
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, onClose]);

  return createPortal(
    <div className="nodrag" style={modalOverlayStyle} onClick={(e) => e.stopPropagation()}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Image size={20} color={PRIMARY_COLOR} />
            <span style={{ color: 'var(--text-body)', fontSize: '15px', fontWeight: '600' }}>
              {modalTitle}
            </span>
            <span style={{ 
              color: '#64748b', fontSize: '12px', 
              background: 'rgba(255,255,255,0.05)', 
              padding: '2px 8px', borderRadius: '4px' 
            }}>
              {ratioLabel}
            </span>
          </div>
          <div style={closeBtnStyle} onClick={onClose} title="关闭" className="modal-tool-btn">
            <X size={18} color="var(--text-primary)" />
          </div>
        </div>

        {/* Canvas */}
        <div style={modalCanvasStyle} onDoubleClick={handleSave}>
          {safeSrc ? (
          <Cropper
            src={safeSrc}
            style={{ height: '100%', width: '100%' }}
            aspectRatio={COVER_ASPECT_RATIO}
            guides={true}
            ref={cropperRef}
            // viewMode 2：1 只约束裁剪框不超出图，没管图相对容器的大小，
            // 连点缩小按钮能把图缩成容器里的一小片。2 要求图至少填满容器一边。
            viewMode={2}
            dragMode="move"
            autoCrop={true}
            background={false}
            autoCropArea={0.9}
            checkCrossOrigin={true}
            zoomOnWheel={false}
          />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
              加载图片中…
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={modalFooterStyle}>
          <div style={toolSectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.02)', padding: '0 12px', borderRadius: '8px', height: '40px' }}>
              <ZoomOut size={16} color="var(--text-secondary)" />
              <input
                type="range" min="0" max="1" step="0.01"
                value={zoomLevel} onChange={handleZoom}
                style={sliderStyle}
                className="custom-range"
              />
              <ZoomIn size={16} color="var(--text-secondary)" />
            </div>
            <div style={dividerStyle}></div>
            <div style={iconBtnStyle(false)} onClick={handleRotate} title="旋转 90°" className="modal-tool-btn">
              <RotateCw size={18} />
            </div>
            <div style={iconBtnStyle(false)} onClick={handleFlipH} title="水平翻转" className="modal-tool-btn">
              <FlipHorizontal size={18} />
            </div>
            <div style={iconBtnStyle(false)} onClick={handleFlipV} title="垂直翻转" className="modal-tool-btn">
              <FlipVertical size={18} />
            </div>
          </div>

          <div style={toolSectionStyle}>
            <button
              style={{ ...actionBtnStyle('secondary'), background: '#374151', color: '#9ca3af' }}
              onClick={handleReset}
              className="modal-reset-btn"
            >
              重置
            </button>
            <button style={actionBtnStyle('primary')} onClick={handleSave} className="modal-confirm-btn">
              {saveBtnText}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .cropper-bg { background-image: none !important; background: #111 !important; }
        .cropper-modal { opacity: 0.7; background-color: #000; }
        .modal-tool-btn:hover { background-color: var(--bg-hover) !important; border-color: rgba(255,255,255,0.3) !important; }
        .modal-tool-btn:active { transform: scale(0.96); }
        .modal-reset-btn:hover { background-color: #4b5563 !important; color: #fff !important; }
        .modal-reset-btn:active { transform: scale(0.96); }
        .modal-confirm-btn:hover { filter: brightness(1.1); }
        .modal-confirm-btn:active { transform: scale(0.96); }
        .custom-range { accent-color: #3b82f6; }
      `}</style>
    </div>,
    document.body
  );
}
